/**
 * Lazy skeleton — global loader watcher.
 *
 * Pairs with the `.lazy-skeleton` utility class in base.css to drop the gray
 * placeholder bg + fade in the media once the resource is ready.
 *
 * The `observer-CFH` snippet adds `.cfh-loaded` to lazy <img> when they finish
 * loading, but:
 *   - Eager <img> (loading="eager", no lazy-src attribute) never go through
 *     observer-CFH, so they never receive `.cfh-loaded`.
 *   - <video> elements aren't tagged with `.cfh-loaded` by observer-CFH either.
 *
 * This script fills both gaps for any media nested inside a `.lazy-skeleton`
 * container. Elements with the `.lazy-skeleton-skip` opt-out class (e.g. hover
 * images that have their own opacity logic) are ignored.
 *
 * The watcher runs at DOMContentLoaded and again whenever the
 * `reactivateLazyObserver` event fires (consistent with observer-CFH lifecycle),
 * so dynamically-injected sections are covered.
 */

const MEDIA_SELECTOR =
  '.lazy-skeleton img:not(.cfh-loaded):not(.lazy-skeleton-skip), .lazy-skeleton video:not(.cfh-loaded):not(.lazy-skeleton-skip)';

const markLoaded = (el) => el.classList.add('cfh-loaded');

const watchMediaLoad = (el) => {
  if (el.tagName === 'IMG') {
    if (el.complete && el.naturalWidth > 0) {
      markLoaded(el);
      return;
    }
    el.addEventListener('load', () => markLoaded(el), { once: true });
    return;
  }

  if (el.tagName === 'VIDEO') {
    // If the video has a poster, treat the poster as the visible "loaded" state.
    // The browser displays the poster before any video bytes arrive, so the user
    // sees a real image straight away — far better than waiting for the first
    // frame (which can take seconds on slow networks).
    // We pre-fetch the poster via Image() to know when it's actually decoded.
    if (el.poster) {
      const posterImg = new Image();
      posterImg.addEventListener('load', () => markLoaded(el), { once: true });
      posterImg.addEventListener('error', () => {
        // Poster failed → fall back to standard video readyState path.
        if (el.readyState >= 2) {
          markLoaded(el);
        } else {
          el.addEventListener('loadeddata', () => markLoaded(el), { once: true });
        }
      }, { once: true });
      posterImg.src = el.poster;
      return;
    }

    // No poster → wait for first frame (HAVE_CURRENT_DATA = readyState 2).
    if (el.readyState >= 2) {
      markLoaded(el);
      return;
    }
    el.addEventListener('loadeddata', () => markLoaded(el), { once: true });
  }
};

const scanLazySkeletonMedia = () => {
  for (const el of document.querySelectorAll(MEDIA_SELECTOR)) {
    watchMediaLoad(el);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scanLazySkeletonMedia, { once: true });
} else {
  scanLazySkeletonMedia();
}

// Re-scan after dynamic DOM injection (Section Rendering API, etc.).
// Same event observer-CFH listens to — keeps the two systems in sync.
document.addEventListener('reactivateLazyObserver', scanLazySkeletonMedia);
